import {useEffect} from 'react';

/**
 * How far bottom-right floating UI must move left to clear an open right-hand
 * drawer, published as a CSS custom property on the document root.
 *
 * A custom property rather than React context on purpose. The one consumer is the
 * support-chat launcher, which is fixed to the viewport's bottom-right corner and
 * rendered far outside any drawer's tree — it has no provider in common with the
 * drawers it needs to avoid, and giving it one would mean threading a provider
 * around the whole app to move a button 640px. The launcher already coordinates
 * with the DOM this way, injecting a stylesheet to shift the Zendesk iframe.
 *
 * Cleared on close and on unmount, so a drawer that unmounts while open (a route
 * change mid-flight) cannot leave the launcher parked off to one side.
 *
 * Every right-hand drawer in the app needs this, and each one currently opts in
 * by calling the hook. When a shared drawer component exists, this belongs
 * inside it: the offset is a consequence of a drawer being open, so a drawer
 * that has to remember to publish it is a drawer that can forget.
 */
export const RIGHT_DRAWER_OFFSET_PROPERTY = '--right-drawer-offset';

export const useRightDrawerOffset = (isOpen: boolean, width: number): void => {
	useEffect(() => {
		if (!isOpen) {
			return undefined;
		}
		const root = document.documentElement;
		root.style.setProperty(RIGHT_DRAWER_OFFSET_PROPERTY, `${width}px`);
		// A block body, not a concise one: removeProperty returns a string, and an
		// implicit return makes this an invalid effect destructor, which React types
		// require to return void.
		return () => {
			root.style.removeProperty(RIGHT_DRAWER_OFFSET_PROPERTY);
		};
	}, [isOpen, width]);
};
